import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  LuClipboardList,
  LuDatabase,
  LuDownload,
  LuEye,
  LuFileClock,
  LuFileText,
  LuHistory,
  LuImage,
  LuInfo,
  LuListOrdered,
  LuMonitor,
  LuPaperclip,
  LuPlay,
  LuRefreshCw,
  LuSparkles,
  LuTag,
  LuTarget,
  LuTrash2,
  LuTriangleAlert,
  LuUsers,
  LuX,
} from 'react-icons/lu';
import '../../styles/modal.css';
import '../../styles/test-cases-overview.css';
import '../../styles/view-test-case-modal.css';
import AISuggestionsModal from './AISuggestionsModal';
import CICDIntegrationModal from '../common/CICDIntegrationModal';
import TestCaseVersionHistory from './TestCaseVersionHistory';
import TestCaseAutoUpdateModal from './TestCaseAutoUpdateModal';
import TestCaseReviewPanel from './TestCaseReviewPanel';
import DeleteConfirmationModal from '../test-plans/DeleteConfirmationModal';
import TestDataTab from './TestDataTab';
import RunTestCaseModal from './RunTestCaseModal';
import TestScreenshotsModal from './TestScreenshotsModal';
import axiosInstance from '../../api/axiosInstance';
import TestCaseAutoUpdateModalCopy from './TestCaseAutoUpdateModalCopy';
import { stepItemToText } from './testCaseStepUtils';
import { toast } from 'react-toast';
import { formatDateTime } from '../../utils/dateTimeFormat';
import { formatDuration } from '../reports/reportData';
import {
  DataTable,
  EmptyState,
  ErrorState,
  Pagination,
  PriorityBadge,
  Select,
  StatusBadge,
} from '../ui';

const MAX_NAME_CHARS = 16;
const HISTORY_PAGE_SIZE_OPTIONS = [10, 20, 50];

const TABS = [
  { id: 'steps', label: 'Test Steps', icon: LuListOrdered },
  { id: 'details', label: 'Details', icon: LuInfo },
  { id: 'history', label: 'Execution History', icon: LuHistory },
  { id: 'testdata', label: 'Test Data', icon: LuDatabase },
];

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]',
].join(', ');

const truncateByChars = (text = '', maxChars = MAX_NAME_CHARS) => {
  const trimmed = (text || '').trim();
  if (!trimmed) return { display: '', full: '', isTruncated: false };

  if (trimmed.length <= maxChars) {
    return { display: trimmed, full: trimmed, isTruncated: false };
  }

  return {
    display: `${trimmed.slice(0, maxChars)}...`,
    full: trimmed,
    isTruncated: true,
  };
};

const humanize = (value) => String(value ?? '').replace(/[_-]+/g, ' ').trim();

const getExecutionStatusLabel = (status) => {
  const key = String(status || '').toLowerCase();
  if (key === 'passed' || key === 'pass') return 'Passed';
  if (key === 'failed' || key === 'fail') return 'Failed';
  return status || 'Unknown';
};

const formatTestSuiteNames = (testSuites) => {
  if (!testSuites || !Array.isArray(testSuites) || testSuites.length === 0) {
    return 'Test Suite Not Linked';
  }
  const names = testSuites.map((suite) => suite?.suite_name).filter(Boolean);
  return names.length > 0 ? names.join(', ') : 'Test Suite Not Linked';
};

const normalizeIndexedItems = (items) => {
  if (!items) return [];
  if (Array.isArray(items)) return items;
  return [items];
};

const normalizeStepDescriptions = (stepDescription) => {
  if (!stepDescription) return [];
  if (Array.isArray(stepDescription)) return stepDescription;
  if (typeof stepDescription === 'string') {
    return stepDescription.split('\n').filter((step) => step.trim() !== '');
  }
  return [stepDescription];
};

const renderIndexedList = (items, emptyMessage) => {
  const list = normalizeIndexedItems(items);
  if (list.length === 0) {
    return <p className="vtc-muted">{emptyMessage}</p>;
  }
  return (
    <ol className="vtc-list">
      {list.map((item, index) => (
        <li key={index} className="vtc-list__item">
          <span className="vtc-list__index" aria-hidden="true">{index + 1}.</span>
          <span className="vtc-list__text">{stepItemToText(item)}</span>
        </li>
      ))}
    </ol>
  );
};

const Section = ({ icon: Icon, title, meta, children }) => (
  <section className="vtc-section">
    <div className="vtc-section__header">
      {Icon && <Icon className="vtc-section__icon" size={15} aria-hidden="true" />}
      <h3 className="vtc-section__title">{title}</h3>
      {meta != null && <span className="vtc-section__meta">{meta}</span>}
    </div>
    {children}
  </section>
);

const DetailFields = ({ fields }) => (
  <dl className="vtc-fields vtc-card">
    {fields.map(({ label, value }) => (
      <div key={label}>
        <dt className="vtc-field__label">{label}</dt>
        <dd className="vtc-field__value">{value || '—'}</dd>
      </div>
    ))}
  </dl>
);

const ViewTestCaseModal = ({ isOpen, onClose, testCase, onEdit, onDelete, onRunTest, onExecutionComplete, onApplySuggestion, onApplyUpdates, onSubmitReview, onPushToFramework, variant = 'modal', }) => {

  const [activeTab, setActiveTab] = useState('steps');
  const [details, setDetails] = useState({});
  const [executionHistory, setExecutionHistory] = useState([]);
  const [historyEnvFilter, setHistoryEnvFilter] = useState('');
  const [historyStatusFilter, setHistoryStatusFilter] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(HISTORY_PAGE_SIZE_OPTIONS[0]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [environments, setEnvironments] = useState([]);
  const [environmentsLoading, setEnvironmentsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  // New state for API-fetched test case data
  const [apiTestCaseData, setApiTestCaseData] = useState(null);
  const [isLoadingTestCase, setIsLoadingTestCase] = useState(false);
  const [testCaseError, setTestCaseError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAISuggestionsModalOpen, setIsAISuggestionsModalOpen] = useState(false);
  const [isVersionHistoryModalOpen, setIsVersionHistoryModalOpen] = useState(false);
  const [isAutoUpdateModalOpen, setIsAutoUpdateModalOpen] = useState(false);
  const [isAutoUpdateModalOpenCopy, setIsAutoUpdateModalOpenCopy] = useState(false);
  const [isCICDModalOpen, setIsCICDModalOpen] = useState(false);
  const [isReviewPanelOpen, setIsReviewPanelOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRunTestCaseModalOpen, setIsRunTestCaseModalOpen] = useState(false);
  const [isScreenshotsModalOpen, setIsScreenshotsModalOpen] = useState(false);
  const [selectedExecutionId, setSelectedExecutionId] = useState(null);
  const historyPollIntervalRef = useRef(null);
  // Guards against out-of-order responses when paging/filtering quickly
  const historyRequestIdRef = useRef(0);
  const bodyRef = useRef(null);
  const panelRef = useRef(null);
  const tabRefs = useRef([]);
  const overlayPointerDownRef = useRef(false);
  const uid = useId();
  const titleId = `${uid}-title`;
  const tabPanelId = `${uid}-panel`;
  const isPage = variant === 'page';
  const hasTestCase = Boolean(testCase);
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;

  // Function to fetch test case details from API
  const fetchTestCaseFromAPI = async () => {
    if (!testCase?.id || !testCase?.project_id) return;

    setIsLoadingTestCase(true);
    setTestCaseError(null);

    try {
      // Use axios instance instead of fetch
      const response = await axiosInstance.get(
        `/test_case/cases_list?project_id=${testCase.project_id}&test_case_id=${testCase.id}`
      );

      const result = response;

      if (result.status === 'success' && result.data && result.data.length > 0) {
        const testCaseData = result.data[0];

        // Transform API response to match expected structure
        const transformedTestCase = {
          id: testCaseData.test_case_id,
          name: testCaseData.name,
          project_id: testCaseData.project_id,
          jira_story_id: testCaseData.jira_story_id,
          priority: testCaseData.priority,
          status: testCaseData.status,
          description: testCaseData.description,
          automation_status: testCaseData.automation_status,
          execution_time: testCaseData.execution_time,
          environment: testCaseData.environment,
          environment_id: testCaseData.environment_id,
          browser: testCaseData.browser,
          device: testCaseData.device,
          tags: testCaseData.tags,
          attachments: testCaseData.attachments,
          script_status: testCaseData.script_status,
          created_by: testCaseData.created_by,
          created_at: testCaseData.created_at,
          updated_at: testCaseData.updated_at,
          steps: testCaseData.steps,
          // Keep original props data for compatibility
          projectName: testCase.projectName,
          suiteName: formatTestSuiteNames(testCaseData.test_suites),
        };

        setApiTestCaseData(transformedTestCase);
      } else {
        throw new Error('No test case data found');
      }
    } catch (error) {
      console.error('Error fetching test case details:', error);
      setTestCaseError(error.message);
      // Fallback to original testCase data if API fails
      setApiTestCaseData({
        ...testCase,
        suiteName: testCase.suiteName || formatTestSuiteNames(testCase.test_suites),
      });
    } finally {
      setIsLoadingTestCase(false);
    }
  };

  // Helper function to refresh test case data with user feedback
  const refreshTestCaseData = async () => {
    setIsRefreshing(true);
    try {
      await fetchTestCaseFromAPI();
    } catch (error) {
      console.error('❌ Error refreshing test case data:', error);
      throw error;
    } finally {
      setIsRefreshing(false);
    }
  };

  // Layout effect so the loading skeleton replaces the list-row data before first paint
  useLayoutEffect(() => {
    if (isOpen && testCase) {
      fetchTestCaseFromAPI();
    }
  }, [isOpen, testCase?.id, testCase?.project_id]);

  useEffect(() => {
    if (!isOpen) {
      setHistoryEnvFilter('');
      setHistoryStatusFilter('');
      setHistoryPage(1);
      setHistoryTotal(0);
      setActiveTab('steps');
      setExecutionHistory([]);
      setHistoryError(null);
      // Drop the previous case so reopening for another one never flashes stale data
      setApiTestCaseData(null);
    }
  }, [isOpen]);

  // Move focus into the dialog on open and hand it back to the trigger on close
  useEffect(() => {
    if (!isOpen || !hasTestCase || isPage) return undefined;
    const previouslyFocused = document.activeElement;
    panelRef.current?.focus({ preventScroll: true });
    return () => {
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [isOpen, hasTestCase, isPage]);

  // All tabs share one scrolling body, so start each tab at the top
  useEffect(() => {
    bodyRef.current?.scrollTo?.({ top: 0 });
  }, [activeTab]);

  // Use API data if available, otherwise fallback to prop data
  const currentTestCase = apiTestCaseData || testCase;
  const truncatedCurrentTestCaseName = truncateByChars(currentTestCase?.name).display;




  // Format test steps for display
  const formatTestSteps = () => {
    if (!currentTestCase || !currentTestCase.steps) return [];

    const steps = currentTestCase.steps;

    if (typeof steps === 'string') {
      return normalizeStepDescriptions(steps).map((desc, index) => ({
        id: index + 1,
        description: stepItemToText(desc) || 'No step description provided',
        expected_result: null,
      }));
    }

    const { step_description = [], expected_result } = steps;
    const descriptions = normalizeStepDescriptions(step_description);

    // Check if expected_result is null or not an array
    const isNoExpectedResults = !expected_result || !Array.isArray(expected_result) || expected_result.length === 0;

    return descriptions.map((desc, index) => ({
      id: index + 1,
      description: stepItemToText(desc) || 'No step description provided',
      expected_result: isNoExpectedResults ? null : (stepItemToText(expected_result[index]) || null)
    }));
  };

  // Commented out - now getting details from main API call
  // useEffect(() => {
  //   const getDetails = async () => {
  //     if (activeTab === 'details' && testCase) {
  //       try {
  //         const caseDetails = await axiosInstance.get(`${API_BASE_URL}/tests/details/${testCase.project_id}/${testCase.id}`);
  //         setDetails(caseDetails?.data);
  //       } catch (error) {
  //         console.error("Error fetching test case details:", error);
  //       }
  //     }
  //   };
  //   getDetails();
  // }, [activeTab, testCase, API_BASE_URL]);

  const fetchEnvironments = useCallback(async (projectId) => {
    if (!projectId) {
      setEnvironments([]);
      return;
    }

    try {
      setEnvironmentsLoading(true);
      const response = await axiosInstance.get(
        `/user_profile/list_environments?project_id=${projectId}`
      );

      if (response.data && response.status_code === 200) {
        setEnvironments(response.data || []);
      } else {
        setEnvironments([]);
      }
    } catch (error) {
      console.error('Error fetching environments:', error);
      setEnvironments([]);
    } finally {
      setEnvironmentsLoading(false);
    }
  }, []);

  // pageOverride lets callers fetch a specific page before the page state has re-rendered
  const fetchExecutionHistory = useCallback(async (pageOverride) => {
    if (!testCase) return;

    const page = pageOverride ?? historyPage;
    const requestId = ++historyRequestIdRef.current;
    setIsLoadingHistory(true);
    setHistoryError(null);

    try {
      const res = await axiosInstance.get('/execute_file/execution_history_list', {
        params: {
          test_case_id: testCase.id || testCase.test_case_id,
          page,
          page_size: historyPageSize,
          ...(historyEnvFilter && { environment_id: historyEnvFilter }),
          ...(historyStatusFilter && { status: historyStatusFilter }),
        },
      });
      if (requestId !== historyRequestIdRef.current) return;

      const dataArray = Array.isArray(res?.data) ? res.data : [];
      const total = res?.total != null ? Number(res.total) || 0 : dataArray.length;
      const lastPage = Math.max(1, Math.ceil(total / historyPageSize));

      // Page fell out of range (e.g. runs removed since the last fetch) — jump to the last valid one
      if (total > 0 && page > lastPage) {
        setHistoryPage(lastPage);
        return;
      }

      setHistoryTotal(total);
      setExecutionHistory(dataArray.map(item => ({
        id: item.execution_id,
        date: item.created_at,
        status: (item.status || '').toLowerCase(),
        environment: item.env || '',
        duration: item.duration || null,
        executed_by: item.user_name || null,
      })));
    } catch (error) {
      if (requestId !== historyRequestIdRef.current) return;
      console.error('Error fetching execution history:', error);
      setHistoryError(error.message || 'Failed to load execution history');
      setExecutionHistory([]);
      setHistoryTotal(0);
    } finally {
      if (requestId === historyRequestIdRef.current) {
        setIsLoadingHistory(false);
      }
    }
  }, [testCase, historyPage, historyPageSize, historyEnvFilter, historyStatusFilter]);

  // Polling reads the latest fetcher so it follows the current page and filters
  const fetchHistoryRef = useRef(fetchExecutionHistory);
  useEffect(() => {
    fetchHistoryRef.current = fetchExecutionHistory;
  }, [fetchExecutionHistory]);

  useEffect(() => {
    if (activeTab === 'history' && isOpen && testCase) {
      fetchExecutionHistory();
    }
  }, [activeTab, isOpen, testCase, fetchExecutionHistory]);

  useEffect(() => {
    if (isOpen && testCase?.project_id) {
      fetchEnvironments(testCase.project_id);
    }
  }, [isOpen, testCase?.project_id, fetchEnvironments]);

  useEffect(() => {
    return () => {
      if (historyPollIntervalRef.current) {
        clearInterval(historyPollIntervalRef.current);
        historyPollIntervalRef.current = null;
      }
    };
  }, []);

  const startExecutionHistoryPolling = useCallback(() => {
    if (historyPollIntervalRef.current) {
      clearInterval(historyPollIntervalRef.current);
    }

    let attempts = 0;
    historyPollIntervalRef.current = setInterval(async () => {
      attempts += 1;
      await fetchHistoryRef.current();
      if (attempts >= 5) {
        clearInterval(historyPollIntervalRef.current);
        historyPollIntervalRef.current = null;
      }
    }, 3000);
  }, []);

  const handleRunTestSuccess = async (executionMode) => {
    setActiveTab('history');
    // The new run lands at the top of page 1
    setHistoryPage(1);
    try {
      await refreshTestCaseData();
      await fetchExecutionHistory(1);
      if (executionMode === 'server') {
        startExecutionHistoryPolling();
      }
      onExecutionComplete?.(testCase?.project_id, testCase?.id || testCase?.test_case_id);
    } catch (error) {
      console.error('Error refreshing data after test run:', error);
    }
  };

  const handleHistoryEnvFilterChange = (e) => {
    setHistoryEnvFilter(e.target.value);
    setHistoryPage(1);
  };

  const handleHistoryStatusFilterChange = (e) => {
    setHistoryStatusFilter(e.target.value);
    setHistoryPage(1);
  };

  const clearHistoryFilters = () => {
    setHistoryEnvFilter('');
    setHistoryStatusFilter('');
    setHistoryPage(1);
  };

  const handleHistoryPageChange = (nextPage) => {
    setHistoryPage(nextPage);
    bodyRef.current?.scrollTo?.({ top: 0 });
  };

  const handleHistoryPageSizeChange = (nextSize) => {
    setHistoryPageSize(nextSize);
    setHistoryPage(1);
  };

  const testSteps = formatTestSteps();

  // Test case details - Updated to use API data
  const testCaseDetails = {
    createdBy: currentTestCase?.created_by || 'Unknown',
    createdAt: currentTestCase?.created_at || new Date().toISOString(),
    updatedAt: currentTestCase?.updated_at || new Date().toISOString(),
    // Updated automation status mapping for API response
    automationStatus: currentTestCase?.automation_status === "generated" ? "Automated" :
                     currentTestCase?.automation_status === "1" ? "Automated" : "Manual",
    executionTime: currentTestCase?.execution_time ? `${currentTestCase.execution_time}s` : 'N/A',
    tags: currentTestCase?.tags
      ? (Array.isArray(currentTestCase.tags)
        ? currentTestCase.tags
        : typeof currentTestCase.tags === 'string' ? currentTestCase.tags.split(',') : [currentTestCase.tags])
      : ['test-case'],
    environment: currentTestCase?.environment || 'Test',
    browser: currentTestCase?.browser || 'Chrome',
    device: currentTestCase?.device || 'Desktop',

    attachments: Array.isArray(currentTestCase?.attachments)
      ? currentTestCase.attachments
      : currentTestCase?.attachments
        ? [currentTestCase.attachments]
        : []
  };


  const handleEditClick = () => {
    if (onEdit) {
      onEdit(currentTestCase?.id || currentTestCase?.test_case_id);
    }
  };


  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };



const confirmDelete = async () => {
  setIsDeleting(true);
  try {
    await axiosInstance.delete(`/test_case/${testCase.id}`);

    // Refresh test case list (best-effort)
    // try {
    //   await axiosInstance.get(`/test_case/cases_list?project_id=${testCase.project_id}`);
    // } catch (refreshErr) {
    //   console.warn('Failed to refresh test case list after delete:', refreshErr);
    // }

    if (onDelete) {
      // Pass project_id to parent so it can refresh the correct project's test cases
      onDelete(testCase.id, testCase.project_id);
    }

    setShowDeleteConfirm(false);
    onClose();
  } catch (error) {
    console.error('Error deleting test case:', error);
    toast.error('Failed to delete test case. Please try again.');
  } finally {
    setIsDeleting(false);
  }
};


  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };


  const handleAISuggestionsClick = () => {
    setIsAISuggestionsModalOpen(true);
  };

  const handleApplySuggestion = async (suggestionResponse) => {
    try {
      if (onApplySuggestion) {
        onApplySuggestion(suggestionResponse);
      } else {
        // Fallback if onApplySuggestion is not provided
        console.log('Applied AI suggestions to test case:', suggestionResponse);
      }

      // Close the AI suggestions modal
      setIsAISuggestionsModalOpen(false);

      // Small delay to show the refresh indicator
      setTimeout(async () => {
        try {
          // Refresh test case data to show updated information
          await refreshTestCaseData();
        } catch (error) {
          console.error('Error refreshing test case data:', error);
        }
      }, 100);

    } catch (error) {
      console.error('Error in handleApplySuggestion:', error);
    }
  };


  // Handle version history button click
  const handleVersionHistoryClick = () => {
    setIsVersionHistoryModalOpen(true);
  };

  // Handle auto-update button click
  const handleAutoUpdateClick = () => {
    setIsAutoUpdateModalOpen(true);
  };
    const handleAutoUpdateClickCopy = () => {
    setIsAutoUpdateModalOpenCopy(true);
  };


  // Handle review panel button click
  const handleReviewPanelClick = () => {
    setIsReviewPanelOpen(true);
  };

  // Handle DevOps Integration button click
  const handleDevOpsIntegrationClick = () => {
    setIsCICDModalOpen(true);
  };

  // Handle viewing screenshots
  const handleViewResults = (executionId) => {
    setSelectedExecutionId(executionId);
    setIsScreenshotsModalOpen(true);
  };

  // Handle submitting a review
  const handleSubmitReview = (reviewData) => {
    if (onSubmitReview) {
      onSubmitReview(reviewData, testCase.id);
    } else {
      // Fallback if onSubmitReview is not provided
      console.log(`Submitted review for test case #${testCase.id}:`, reviewData);
      if (reviewData.action === 'assign') {
        toast.success(`Assigned ${reviewData.reviewers.length} reviewers to test case`);
      } else {
        toast.success(`Submitted ${reviewData.status} review for test case`);
      }
    }
  };

  // Handle applying updates from auto-update modal
  const handleApplyUpdates = (updateData) => {
    // Show success toast notification
    // const toast = document.createElement('div');
    // toast.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 animate-pulse';
    // toast.innerHTML = `
    //   <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    //     <polyline points="20 6 9 17 4 12"></polyline>
    //   </svg>
    //   <span>Script applied successfully!</span>
    // `;
    // document.body.appendChild(toast);
    // setTimeout(() => {
    //   toast.remove();
    // }, 3000);

    // if (onApplyUpdates) {
    //   onApplyUpdates(updateData, testCase.id);
    }
  // };

  // Only close when the press started on the backdrop, so a text-selection drag that
  // ends outside the panel doesn't dismiss it. detail === 0 covers the synthetic
  // click dispatched by the global Escape handler.
  const handleOverlayMouseDown = (e) => {
    overlayPointerDownRef.current = e.target === e.currentTarget;
  };

  const handleOverlayClick = (e) => {
    const startedOnOverlay = overlayPointerDownRef.current || e.detail === 0;
    overlayPointerDownRef.current = false;
    if (e.target === e.currentTarget && startedOnOverlay) onClose();
  };

  const handlePanelKeyDown = (e) => {
    if (isPage || e.key !== 'Tab' || showDeleteConfirm || !panelRef.current) return;
    const focusable = Array.from(panelRef.current.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter((el) => el.offsetParent !== null && el.getAttribute('tabindex') !== '-1');
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === panelRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const handleTabKeyDown = (e) => {
    const index = TABS.findIndex((tab) => tab.id === activeTab);
    let next = null;
    if (e.key === 'ArrowRight') next = (index + 1) % TABS.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + TABS.length) % TABS.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = TABS.length - 1;
    if (next === null) return;
    e.preventDefault();
    setActiveTab(TABS[next].id);
    tabRefs.current[next]?.focus();
  };

  if (!isOpen || !testCase) return null;

  const renderShell = (inner) => {
    if (isPage) {
      return <div className="rt-view-as-page">{inner}</div>;
    }
    return createPortal(inner, document.body);
  };

  // A background refresh keeps the current content (and any open child modal) mounted
  const showSkeleton = isLoadingTestCase && !isRefreshing;
  const caseId = currentTestCase?.id || currentTestCase?.test_case_id;
  const tags = testCaseDetails.tags.map((tag) => String(tag).trim()).filter(Boolean);
  const hasHistoryFilters = Boolean(historyEnvFilter || historyStatusFilter);

  const closeButton = (
    <button
      type="button"
      className="rt-icon-btn rt-icon-btn--md modal-close vtc-close"
      onClick={onClose}
      aria-label="Close"
      title="Close"
    >
      <LuX size={18} aria-hidden="true" />
    </button>
  );

  const overviewFields = [
    { label: 'Project', value: currentTestCase?.projectName || 'N/A' },
    { label: 'Test Suite', value: currentTestCase?.suiteName || 'Test Suite Not Linked' },
    ...(currentTestCase?.jira_story_id ? [{ label: 'Jira Story', value: currentTestCase.jira_story_id }] : []),
    { label: 'Created By', value: testCaseDetails.createdBy },
    { label: 'Created At', value: formatDateTime(testCaseDetails.createdAt) },
    { label: 'Last Updated', value: formatDateTime(testCaseDetails.updatedAt) },
    { label: 'Automation Status', value: testCaseDetails.automationStatus },
    { label: 'Execution Time', value: testCaseDetails.executionTime },
  ];

  const environmentFields = [
    { label: 'Environment', value: testCaseDetails.environment },
    { label: 'Browser', value: testCaseDetails.browser },
    { label: 'Device', value: testCaseDetails.device },
  ];

  const historyColumns = [
    {
      key: 'status',
      header: 'Status',
      width: '120px',
      render: (row) => (
        <StatusBadge status={row.status}>{getExecutionStatusLabel(row.status)}</StatusBadge>
      ),
    },
    {
      key: 'date',
      header: 'Executed',
      render: (row) => (
        <div className="vtc-run">
          <span className="vtc-run__date">{formatDateTime(row.date)}</span>
          <span className="vtc-run__id">Run #{row.id}</span>
        </div>
      ),
    },
    {
      key: 'environment',
      header: 'Environment',
      render: (row) => (row.environment
        ? <span className="vtc-chip" title={row.environment}>{row.environment}</span>
        : <span className="vtc-muted">N/A</span>),
    },
    {
      key: 'duration',
      header: 'Duration',
      render: (row) => <span className="rt-num">{formatDuration(row.duration)}</span>,
    },
    {
      key: 'executed_by',
      header: 'Executed By',
      render: (row) => (
        <span className="vtc-run__user" title={row.executed_by || undefined}>
          {row.executed_by || 'Unknown'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: <span className="rt-visually-hidden">Actions</span>,
      render: (row) => (
        <button
          type="button"
          className="rt-btn rt-btn--secondary rt-btn--sm"
          onClick={() => handleViewResults(row.id)}
          aria-label={`View results for run #${row.id}`}
        >
          <LuEye size={14} aria-hidden="true" />
          <span>View Results</span>
        </button>
      ),
    },
  ];

  const renderStepsTab = () => (
    <div className="vtc-stack">
      <Section icon={LuClipboardList} title="Preconditions">
        <div className="vtc-card">
          {renderIndexedList(currentTestCase?.steps?.prerequisites, 'No preconditions specified.')}
        </div>
      </Section>

      <Section
        icon={LuListOrdered}
        title="Steps"
        meta={testSteps.length > 0 ? `${testSteps.length} ${testSteps.length === 1 ? 'step' : 'steps'}` : null}
      >
        {testSteps.length > 0 ? (
          <ol className="vtc-steps">
            {testSteps.map((step, index) => (
              <li key={step.id} className="vtc-step">
                <span className="vtc-step__num" aria-hidden="true">{index + 1}</span>
                <span className="vtc-step__text">
                  <span className="rt-visually-hidden">Step {index + 1}: </span>
                  {step.description}
                </span>
              </li>
            ))}
          </ol>
        ) : (
          <EmptyState
            icon={<LuListOrdered size={20} />}
            title="No test steps"
            description="No test steps defined for this test case."
          />
        )}
      </Section>

      <Section icon={LuTarget} title="Expected Result">
        <div className="vtc-card">
          {renderIndexedList(currentTestCase?.steps?.expected_result, 'No expected result provided.')}
        </div>
      </Section>
    </div>
  );

  const renderDetailsTab = () => (
    <div className="vtc-stack">
      <Section icon={LuFileText} title="Description">
        <div className="vtc-card">
          {currentTestCase?.description
            ? <p className="vtc-prose">{currentTestCase.description}</p>
            : <p className="vtc-muted">No description provided.</p>}
        </div>
      </Section>

      <Section icon={LuInfo} title="Test Case Information">
        <DetailFields fields={overviewFields} />
      </Section>

      <Section icon={LuMonitor} title="Environment">
        <DetailFields fields={environmentFields} />
      </Section>

      <Section icon={LuTag} title="Tags">
        <div className="vtc-tags">
          {tags.map((tag, index) => (
            <span key={`${tag}-${index}`} className="vtc-chip">{tag}</span>
          ))}
        </div>
      </Section>

      <Section
        icon={LuPaperclip}
        title="Attachments"
        meta={testCaseDetails.attachments.length > 0 ? testCaseDetails.attachments.length : null}
      >
        {testCaseDetails.attachments.length > 0 ? (
          <ul className="vtc-attachments">
            {testCaseDetails.attachments.map((attachment, index) => {
              const name = typeof attachment === 'string' ? attachment : attachment?.name || 'Attachment';
              const isImage = typeof attachment?.type === 'string' && attachment.type.includes('image');
              return (
                <li key={attachment?.id ?? `${name}-${index}`} className="vtc-attachment">
                  <span className="vtc-attachment__icon" aria-hidden="true">
                    {isImage ? <LuImage size={16} /> : <LuFileText size={16} />}
                  </span>
                  <div className="vtc-attachment__info">
                    <div className="vtc-attachment__name" title={name}>{name}</div>
                    {attachment?.size && <div className="vtc-attachment__meta">{attachment.size}</div>}
                  </div>
                  <button
                    type="button"
                    className="rt-icon-btn rt-icon-btn--sm"
                    aria-label={`Download ${name}`}
                    title="Download"
                  >
                    <LuDownload size={16} aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="vtc-card">
            <p className="vtc-muted">No attachments available.</p>
          </div>
        )}
      </Section>
    </div>
  );

  const renderHistoryTable = () => {
    if (historyError) {
      return (
        <ErrorState
          title="Couldn't load execution history"
          description={historyError}
          onRetry={() => fetchExecutionHistory()}
        />
      );
    }
    if (!isLoadingHistory && executionHistory.length === 0) {
      return hasHistoryFilters ? (
        <EmptyState
          icon={<LuHistory size={20} />}
          title="No matching executions"
          description="No runs match the selected environment and status."
          action={(
            <button type="button" className="rt-btn rt-btn--secondary rt-btn--sm" onClick={clearHistoryFilters}>
              Clear filters
            </button>
          )}
        />
      ) : (
        <EmptyState
          icon={<LuHistory size={20} />}
          title="No executions yet"
          description="No execution history available for this test case."
        />
      );
    }
    return (
      <DataTable
        columns={historyColumns}
        rows={executionHistory}
        rowKey="id"
        loading={isLoadingHistory && executionHistory.length === 0}
        skeletonRows={5}
      />
    );
  };

  const renderHistoryTab = () => (
    <div className="vtc-history">
      <div className="vtc-toolbar">
        <div className="vtc-toolbar__title">
          <h3 className="vtc-section__title">Recent Executions</h3>
          {historyTotal > 0 && (
            <span className="vtc-section__meta">
              {historyTotal.toLocaleString()} {historyTotal === 1 ? 'run' : 'runs'}
            </span>
          )}
        </div>
        <div className="vtc-filters">
          <Select
            size="sm"
            value={historyEnvFilter}
            onChange={handleHistoryEnvFilterChange}
            aria-label="Filter by environment"
            disabled={environmentsLoading}
          >
            <option value="">
              {environmentsLoading ? 'Loading...' : 'All Environments'}
            </option>
            {environments.map((env) => (
              <option key={env.id} value={env.id}>
                {env.environment_name}
              </option>
            ))}
          </Select>
          <Select
            size="sm"
            value={historyStatusFilter}
            onChange={handleHistoryStatusFilterChange}
            aria-label="Filter by status"
          >
            <option value="">All Statuses</option>
            <option value="PASSED">Passed</option>
            <option value="FAILED">Failed</option>
          </Select>
        </div>
      </div>

      <div
        className={`vtc-history__table${isLoadingHistory && executionHistory.length > 0 ? ' vtc-history__table--busy' : ''}`}
        aria-busy={isLoadingHistory || undefined}
      >
        {renderHistoryTable()}
      </div>

      {!historyError && (
        <Pagination
          page={historyPage}
          pageSize={historyPageSize}
          total={historyTotal}
          onPageChange={handleHistoryPageChange}
          onPageSizeChange={handleHistoryPageSizeChange}
          pageSizeOptions={HISTORY_PAGE_SIZE_OPTIONS}
          loading={isLoadingHistory}
        />
      )}
    </div>
  );

  const renderSkeleton = () => (
    <>
      <span className="rt-visually-hidden" role="status">Loading test case details...</span>
      {!isPage && (
        <div className="vtc-header">
          <div className="vtc-header__main" aria-hidden="true">
            <span className="rt-skeleton vtc-sk vtc-sk--eyebrow" />
            <span className="rt-skeleton vtc-sk vtc-sk--title" />
            <span className="rt-skeleton vtc-sk vtc-sk--meta" />
          </div>
          {closeButton}
        </div>
      )}
      <div className="vtc-sk-tabs" aria-hidden="true">
        {TABS.map((tab) => <span key={tab.id} className="rt-skeleton vtc-sk vtc-sk--tab" />)}
      </div>
      <div className="vtc-body" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((row) => <span key={row} className="rt-skeleton vtc-sk vtc-sk--row" />)}
      </div>
    </>
  );

  const renderContent = () => (
    <>
      <div className={`vtc-header${isPage ? ' rt-entity-chrome-hidden' : ''}`}>
        <div className="vtc-header__main">
          <p className="vtc-eyebrow">
            <span>Test Case</span>
            {caseId && <span className="vtc-eyebrow__id">#{caseId}</span>}
          </p>
          <h2 id={titleId} className="vtc-title" title={currentTestCase?.name}>
            {currentTestCase?.name || 'Untitled test case'}
          </h2>
          <dl className="vtc-meta">
            {currentTestCase?.priority && (
              <div className="vtc-meta__item">
                <dt>Priority</dt>
                <dd><PriorityBadge priority={humanize(currentTestCase.priority)} /></dd>
              </div>
            )}
            {currentTestCase?.status && (
              <div className="vtc-meta__item">
                <dt>Status</dt>
                <dd><StatusBadge status={humanize(currentTestCase.status)} /></dd>
              </div>
            )}
            <div className="vtc-meta__item">
              <dt>Type</dt>
              <dd><span className="rt-badge rt-badge--neutral">{testCaseDetails.automationStatus}</span></dd>
            </div>
          </dl>
        </div>
        {closeButton}
      </div>

      <div className="vtc-tabs-row">
        <div className="vtc-tabs" role="tablist" aria-label="Test case sections" onKeyDown={handleTabKeyDown}>
          {TABS.map((tab, index) => {
            const selected = activeTab === tab.id;
            const Icon = tab.icon;
            const count = tab.id === 'steps'
              ? testSteps.length
              : tab.id === 'history' && historyTotal > 0 ? historyTotal : null;
            return (
              <button
                key={tab.id}
                ref={(el) => { tabRefs.current[index] = el; }}
                type="button"
                role="tab"
                id={`${uid}-tab-${tab.id}`}
                aria-selected={selected}
                aria-controls={tabPanelId}
                tabIndex={selected ? 0 : -1}
                className={`vtc-tab${selected ? ' vtc-tab--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={15} aria-hidden="true" />
                <span>{tab.label}</span>
                {count !== null && <span className="vtc-tab__count">{count}</span>}
              </button>
            );
          })}
        </div>
        {isRefreshing && (
          <span className="vtc-refreshing" role="status">
            <LuRefreshCw className="rt-spin" size={13} aria-hidden="true" />
            Refreshing...
          </span>
        )}
      </div>

      <div
        id={tabPanelId}
        role="tabpanel"
        aria-labelledby={`${uid}-tab-${activeTab}`}
        ref={bodyRef}
        tabIndex={0}
        className="vtc-body"
      >
        {testCaseError && (
          <div className="vtc-alert" role="alert">
            <LuTriangleAlert size={16} aria-hidden="true" />
            <span>Couldn't load the latest details. Showing the information available from the list.</span>
            <button
              type="button"
              className="rt-btn rt-btn--ghost rt-btn--sm"
              onClick={() => { refreshTestCaseData().catch(() => {}); }}
              disabled={isRefreshing}
            >
              Retry
            </button>
          </div>
        )}

        {activeTab === 'steps' && renderStepsTab()}
        {activeTab === 'details' && renderDetailsTab()}
        {activeTab === 'history' && renderHistoryTab()}
        {activeTab === 'testdata' && (
          <TestDataTab
            testCaseId={currentTestCase?.id || testCase?.id || testCase?.test_case_id}
            testCaseName={truncatedCurrentTestCaseName || truncateByChars(testCase?.name).display}
          />
        )}
      </div>

      <div className="vtc-footer">
        <button
          type="button"
          className="rt-btn rt-btn--ghost rt-btn--sm vtc-btn-danger"
          onClick={handleDeleteClick}
          disabled={isDeleting}
          title="Delete"
        >
          <LuTrash2 size={15} aria-hidden="true" />
          <span className="vtc-btn__label">Delete</span>
        </button>

        <div className="vtc-footer__actions">
          <button
            type="button"
            className="rt-btn rt-btn--secondary rt-btn--sm"
            onClick={handleVersionHistoryClick}
            title="Version History"
          >
            <LuFileClock size={15} aria-hidden="true" />
            <span className="vtc-btn__label">Version History</span>
          </button>
          <button
            type="button"
            className="rt-btn rt-btn--secondary rt-btn--sm"
            onClick={handleAutoUpdateClick}
            title="Self-Healing"
          >
            <LuRefreshCw size={15} aria-hidden="true" />
            <span className="vtc-btn__label">Self-Healing</span>
          </button>
          <button
            type="button"
            className="rt-btn rt-btn--secondary rt-btn--sm"
            onClick={handleReviewPanelClick}
            title="Review"
          >
            <LuUsers size={15} aria-hidden="true" />
            <span className="vtc-btn__label">Review</span>
          </button>
          <button
            type="button"
            className="rt-btn rt-btn--secondary rt-btn--sm"
            onClick={handleAISuggestionsClick}
            title="AI Suggestions"
          >
            <LuSparkles size={15} aria-hidden="true" />
            <span className="vtc-btn__label">AI Suggestions</span>
          </button>
          <button
            type="button"
            className="rt-btn rt-btn--primary rt-btn--sm"
            onClick={() => {
              setIsRunTestCaseModalOpen(true);
            }}
            title="Run Test"
          >
            <LuPlay size={15} aria-hidden="true" />
            <span>Run Test</span>
          </button>
        </div>
      </div>

      {/* Delete confirmation modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        itemName={truncatedCurrentTestCaseName || truncateByChars(testCase?.name).display}
        itemType="test case"
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
        isProcessing={isDeleting}
      />
    </>
  );

  return renderShell(
    <>
      <div
        data-rt-modal-overlay
        className={isPage ? 'vtc-page-host' : 'view-test-case-modal-overlay'}
        onMouseDown={isPage ? undefined : handleOverlayMouseDown}
        onClick={isPage ? undefined : handleOverlayClick}
      >
        <div
          ref={panelRef}
          className={`vtc-panel${isPage ? ' vtc-panel--page' : ''}`}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handlePanelKeyDown}
          role={isPage ? undefined : 'dialog'}
          aria-modal={isPage ? undefined : 'true'}
          aria-labelledby={!isPage && !showSkeleton ? titleId : undefined}
          aria-label={!isPage && showSkeleton ? 'Loading test case' : undefined}
          aria-busy={showSkeleton || undefined}
          tabIndex={isPage ? undefined : -1}
        >
          {showSkeleton ? renderSkeleton() : renderContent()}
        </div>
      </div>

      {/* AI Suggestions Modal */}
      <AISuggestionsModal
        isOpen={isAISuggestionsModalOpen}
        onClose={() => setIsAISuggestionsModalOpen(false)}
        testCase={currentTestCase}
        onApplySuggestion={handleApplySuggestion}
      />

      {/* Version History Modal */}
      <TestCaseVersionHistory
        isOpen={isVersionHistoryModalOpen}
        onClose={() => setIsVersionHistoryModalOpen(false)}
        testCase={currentTestCase}
      />

      {/* Auto-Update Modal */}
{ isAutoUpdateModalOpen &&     <TestCaseAutoUpdateModal
        isOpen={isAutoUpdateModalOpen}
        onClose={() => setIsAutoUpdateModalOpen(false)}
        testCaseId={currentTestCase?.id || apiTestCaseData?.id || testCase?.id}
        onSuccess={handleApplyUpdates}
      />}

      {/* Review Panel */}
      <TestCaseReviewPanel
        isOpen={isReviewPanelOpen}
        onClose={() => setIsReviewPanelOpen(false)}
        testCase={currentTestCase}
        onSubmitReview={handleSubmitReview}
      />

      {/* CI/CD Integration Modal */}
      <CICDIntegrationModal
        isOpen={isCICDModalOpen}
        onClose={() => setIsCICDModalOpen(false)}
        testSuites={[
          { id: 1, name: 'Login Functionality', testCases: 3 },
          { id: 2, name: 'Product Search', testCases: 2 },
          { id: 3, name: 'Checkout Process', testCases: 4 }
        ]}
        onPushToFramework={onPushToFramework}
      />

      {/* Run Test Case Modal */}
      <RunTestCaseModal
        isOpen={isRunTestCaseModalOpen}
        onClose={() => setIsRunTestCaseModalOpen(false)}
        onSuccess={handleRunTestSuccess}
        testCaseId={currentTestCase?.id || testCase?.id}
        testCaseName={truncatedCurrentTestCaseName || truncateByChars(testCase?.name).display}
        projectId={currentTestCase?.project_id || testCase?.project_id}
        projectName={currentTestCase?.projectName || testCase?.projectName}
        ai_generated={testCase?.ai_generated}
        environment={currentTestCase?.environment || testCase?.environment}
        environmentId={currentTestCase?.environment_id || testCase?.environment_id}
      />

      {/* Test Screenshots Modal */}
      <TestScreenshotsModal
        isOpen={isScreenshotsModalOpen}
        onClose={() => setIsScreenshotsModalOpen(false)}
        executionId={selectedExecutionId}
        testCaseId={currentTestCase?.id || testCase?.id}
        testCaseName={truncatedCurrentTestCaseName || truncateByChars(testCase?.name).display}
      />

        <TestCaseAutoUpdateModalCopy
        isOpen={isAutoUpdateModalOpenCopy}
        onClose={() => setIsAutoUpdateModalOpenCopy(false)}
        testCase={currentTestCase}
        onApplyUpdates={handleApplyUpdates}
      />
    </>
  );
};

export default ViewTestCaseModal;
